import React from 'react';
import { Home, ListChecks, Calculator, Menu, X, UserPlus, Camera, Briefcase, Tag } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion'; 

interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  isHighlighted?: boolean;
  onClick?: () => void;
}

function NavLink({ to, icon, label, isActive, isHighlighted, onClick }: NavLinkProps) {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onClick) onClick();
  };

  return (
    <Link
      to={to}
      onClick={handleClick} 
      className={`flex flex-col items-center p-2 pt-1.5 transition-colors ${
        isActive 
          ? 'text-primary-600 dark:text-primary-400'
          : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
      } relative`}
      aria-label={label}
      title={label}
    >
      {isHighlighted && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: [0.7, 1, 0.7], 
            scale: [1, 1.15, 1],
            boxShadow: [
              '0 0 0 0 rgba(239, 65, 54, 0.4)',
              '0 0 0 10px rgba(239, 65, 54, 0)',
              '0 0 0 0 rgba(239, 65, 54, 0.4)'
            ]
          }}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            repeatType: "loop"
          }}
          className="absolute inset-[-4px] rounded-full bg-primary-100 dark:bg-primary-900/20 z-[-1]"
        />
      )}
      <div className="w-7 h-7 flex items-center justify-center mb-0.5">
        {React.cloneElement(icon as React.ReactElement, {
          className: "w-5 h-5"
        })}
      </div>
      <span className="text-xs font-medium">{label}</span>
    </Link>
  );
}

interface MobileNavBarProps {
  onOpenMenu: () => void;
  isMenuOpen: boolean;
}

export function MobileNavBar({ onOpenMenu, isMenuOpen }: MobileNavBarProps) {
  const location = useLocation();
  const [hasClickedBudget, setHasClickedBudget] = React.useState(() => {
    return localStorage.getItem('hasClickedBudget') === 'true';
  });
  
  const handleBudgetClick = () => {
    if (!hasClickedBudget) {
      localStorage.setItem('hasClickedBudget', 'true');
      setHasClickedBudget(true);
    }
  };

  // Determine if we're on an ultra-narrow screen
  const [isNarrow, setIsNarrow] = React.useState(false);

  // Update state on window resize
  React.useEffect(() => {
    const checkScreenWidth = () => {
      setIsNarrow(window.innerWidth < 350);
    };
    
    // Initial check
    checkScreenWidth();
    
    // Listen for resize events
    window.addEventListener('resize', checkScreenWidth);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 10px)' }}>
      <div className="bg-white/95 dark:bg-gray-900/95 border-t border-gray-200/20 dark:border-gray-700/20 backdrop-blur-lg py-3 px-2 shadow-lg pb-safe">
        <div className="grid grid-cols-6 gap-0">
          <NavLink
            to="/"
            icon={<Home className="w-6 h-6" />}
            label="Accueil"
            isActive={location.pathname === '/'}
          />
          
          <NavLink
            to="/simulateur-budget"
            icon={<Calculator className="w-6 h-6" />}
            label="Budget" 
            isActive={location.pathname === '/simulateur-budget'}
            isHighlighted={!hasClickedBudget}
            onClick={handleBudgetClick}
          />

          <NavLink
            to="/media"
            icon={<Camera className="w-6 h-6" />}
            label="Photos" 
            isActive={location.pathname === '/media'}
          />
          
          <NavLink
            to="/matching"
            icon={<UserPlus className="w-6 h-6" />}
            label="PVTmates"
            isActive={location.pathname === '/matching'}
          />
          
          <NavLink
            to="/jobs"
            icon={<Briefcase className="w-6 h-6" />}
            label="Emploi" 
            isActive={location.pathname === '/jobs' || location.pathname.startsWith('/jobs/')}
          />
          
          <button
            onClick={onOpenMenu}
            className="flex flex-col items-center p-2 pt-1.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            title={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            <div className="w-7 h-7 flex items-center justify-center mb-0.5">
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </div>
            <span className="text-xs font-medium">{isMenuOpen ? "Fermer" : "Menu"}</span>
          </button>
        </div>
        <div className="h-2"></div>
      </div>
    </div>
  );
}